import { 
  FilterState, 
  ReportPeriod, 
  ComparisonType, 
  DateRange,
  QuarterPeriod,
  YearPeriod,
  MonthPeriod,
  MetricThreshold,
  SortConfig,
  GroupConfig,
  SavedView
} from '../types/filterTypes';

/**
 * Converts a month period to a date range
 * @param monthPeriod The month period to convert
 * @returns DateRange for the specified month
 */
export const monthPeriodToDateRange = (monthPeriod: MonthPeriod): DateRange => {
  const { month, year } = monthPeriod;
  
  // Create date for first day of the month
  const startDate = new Date(year, month - 1, 1);
  
  // Create date for last day of the month
  const endDate = new Date(year, month, 0);
  
  return {
    start: startDate.toISOString().split('T')[0],
    end: endDate.toISOString().split('T')[0]
  };
};

/**
 * Converts a quarter period to a date range
 * @param quarterPeriod The quarter period to convert
 * @returns DateRange for the specified quarter
 */
export const quarterPeriodToDateRange = (quarterPeriod: QuarterPeriod): DateRange => {
  const { quarter, year } = quarterPeriod;
  
  // Calculate the start and end months for the quarter
  const startMonth = (quarter - 1) * 3 + 1;
  const endMonth = quarter * 3;
  
  // Create date for first day of the quarter
  const startDate = new Date(year, startMonth - 1, 1);
  
  // Create date for last day of the quarter
  const endDate = new Date(year, endMonth, 0);
  
  return {
    start: startDate.toISOString().split('T')[0],
    end: endDate.toISOString().split('T')[0]
  };
};

/**
 * Converts a year period to a date range
 * @param yearPeriod The year period to convert
 * @returns DateRange for the specified year (fiscal or calendar)
 */
export const yearPeriodToDateRange = (yearPeriod: YearPeriod): DateRange => {
  const { year, fiscal } = yearPeriod;
  
  if (fiscal) {
    // Fiscal year typically starts in October of the previous year
    const startDate = new Date(year - 1, 9, 1); // October 1st of previous year
    const endDate = new Date(year, 8, 30); // September 30th of current year
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  } else {
    // Calendar year
    const startDate = new Date(year, 0, 1); // January 1st
    const endDate = new Date(year, 11, 31); // December 31st
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  }
};

/**
 * Get a date range based on the current filter state
 * @param filterState The current filter state
 * @returns DateRange representing the selected period
 */
export const getDateRangeFromFilterState = (filterState: FilterState): DateRange => {
  switch (filterState.reportPeriod) {
    case ReportPeriod.MONTHLY:
      if (!filterState.monthPeriod) {
        throw new Error('Month period is required for monthly reports');
      }
      return monthPeriodToDateRange(filterState.monthPeriod);
    
    case ReportPeriod.QUARTERLY:
      if (!filterState.quarterPeriod) {
        throw new Error('Quarter period is required for quarterly reports');
      }
      return quarterPeriodToDateRange(filterState.quarterPeriod);
    
    case ReportPeriod.ANNUAL:
      if (!filterState.yearPeriod) {
        throw new Error('Year period is required for annual reports');
      }
      return yearPeriodToDateRange(filterState.yearPeriod);
    
    case ReportPeriod.CUSTOM:
      if (!filterState.customDateRange) {
        throw new Error('Custom date range is required for custom period reports');
      }
      return filterState.customDateRange;
    
    default:
      throw new Error(`Unsupported report period: ${filterState.reportPeriod}`);
  }
};

/**
 * Get a comparison date range based on the current filter state
 * @param filterState The current filter state
 * @returns DateRange representing the comparison period
 */
export const getComparisonDateRange = (filterState: FilterState): DateRange | null => {
  if (filterState.comparisonType === ComparisonType.NONE) {
    return null;
  }
  
  // If explicit comparison period is provided, use it
  if (filterState.comparisonPeriod) {
    if (filterState.comparisonPeriod.monthPeriod) {
      return monthPeriodToDateRange(filterState.comparisonPeriod.monthPeriod);
    }
    
    if (filterState.comparisonPeriod.quarterPeriod) {
      return quarterPeriodToDateRange(filterState.comparisonPeriod.quarterPeriod);
    }
    
    if (filterState.comparisonPeriod.yearPeriod) {
      return yearPeriodToDateRange(filterState.comparisonPeriod.yearPeriod);
    }
    
    if (filterState.comparisonPeriod.customDateRange) {
      return filterState.comparisonPeriod.customDateRange;
    }
  }
  
  // Calculate comparison period based on the current period and comparison type
  const currentDateRange = getDateRangeFromFilterState(filterState);
  
  switch (filterState.comparisonType) {
    case ComparisonType.MOM: { // Month over Month
      if (!filterState.monthPeriod) {
        return null;
      }
      
      let prevMonth = filterState.monthPeriod.month - 1;
      let prevYear = filterState.monthPeriod.year;
      
      if (prevMonth < 1) {
        prevMonth = 12;
        prevYear -= 1;
      }
      
      return monthPeriodToDateRange({ month: prevMonth, year: prevYear });
    }
    
    case ComparisonType.YOY: { // Year over Year
      // Simply subtract one year from the current date range
      const start = currentDateRange.start 
        ? new Date(currentDateRange.start) 
        : null;
      
      const end = currentDateRange.end 
        ? new Date(currentDateRange.end) 
        : null;
      
      if (start) {
        start.setFullYear(start.getFullYear() - 1);
      }
      
      if (end) {
        end.setFullYear(end.getFullYear() - 1);
      }
      
      return {
        start: start ? start.toISOString().split('T')[0] : null,
        end: end ? end.toISOString().split('T')[0] : null
      };
    }
    
    case ComparisonType.QOQ: { // Quarter over Quarter
      if (!filterState.quarterPeriod) {
        return null;
      }
      
      let prevQuarter = filterState.quarterPeriod.quarter - 1;
      let prevYear = filterState.quarterPeriod.year;
      
      if (prevQuarter < 1) {
        prevQuarter = 4;
        prevYear -= 1;
      }
      
      return quarterPeriodToDateRange({ 
        quarter: prevQuarter as 1 | 2 | 3 | 4, 
        year: prevYear 
      });
    }
    
    default:
      return null;
  }
};

/**
 * Convert a filter state to a query string for API requests
 * @param filterState The filter state to convert
 * @returns Object with query parameters for the API
 */
export const filterStateToQueryParams = (filterState: FilterState): Record<string, string | string[]> => {
  const params: Record<string, string | string[]> = {};
  
  // Date range
  try {
    const dateRange = getDateRangeFromFilterState(filterState);
    if (dateRange.start) {
      params.startDate = dateRange.start;
    }
    if (dateRange.end) {
      params.endDate = dateRange.end;
    }
  } catch (error) {
    console.error('Error getting date range:', error);
  }
  
  // Source filters
  if (filterState.sources && filterState.sources.length > 0) {
    params.sources = filterState.sources;
  }
  
  // Campaign filters
  if (filterState.campaigns && filterState.campaigns.length > 0) {
    params.campaigns = filterState.campaigns;
  }
  
  // Trade filters
  if (filterState.trades && filterState.trades.length > 0) {
    params.trades = filterState.trades;
  }
  
  // Metric thresholds
  if (filterState.metricThresholds && filterState.metricThresholds.length > 0) {
    // Convert thresholds to a format the API can use
    params.metricFilters = filterState.metricThresholds.map(threshold => 
      `${threshold.metric}:${threshold.operator}:${threshold.value}`
    );
  }
  
  // Filter logic
  params.filterLogic = filterState.filterLogic;
  
  // Sort config
  if (filterState.sortConfig && filterState.sortConfig.length > 0) {
    params.sort = filterState.sortConfig.map(sort => 
      `${sort.field}:${sort.direction}`
    );
  }
  
  // Group config
  if (filterState.groupConfig) {
    params.groupBy = filterState.groupConfig.field;
    
    if (filterState.groupConfig.subGroupBy) {
      params.subGroupBy = filterState.groupConfig.subGroupBy;
    }
  }
  
  // Report period
  params.reportType = filterState.reportPeriod;
  
  // Comparison
  if (filterState.comparisonType !== ComparisonType.NONE) {
    params.comparisonType = filterState.comparisonType;
    
    // Add comparison date range if applicable
    const comparisonDateRange = getComparisonDateRange(filterState);
    if (comparisonDateRange && comparisonDateRange.start) {
      params.comparisonStartDate = comparisonDateRange.start;
    }
    if (comparisonDateRange && comparisonDateRange.end) {
      params.comparisonEndDate = comparisonDateRange.end;
    }
  }
  
  return params;
};

/**
 * Creates a new saved view from the current filter state
 * @param filterState Current filter state
 * @param name Name for the saved view
 * @param description Optional description
 * @param userId ID of the current user
 * @returns SavedView object
 */
export const createSavedView = (
  filterState: FilterState,
  name: string,
  description = '',
  userId: string
): SavedView => {
  return {
    id: `view_${Date.now()}`,
    name,
    description,
    filterState,
    createdBy: userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

/**
 * Generate a human-readable summary of the current filter state
 * @param filterState The filter state to summarize
 * @returns String describing the current filters
 */
export const generateFilterSummary = (filterState: FilterState): string => {
  const parts: string[] = [];
  
  // Period
  switch (filterState.reportPeriod) {
    case ReportPeriod.MONTHLY:
      if (filterState.monthPeriod) {
        const month = new Date(0, filterState.monthPeriod.month - 1).toLocaleString('default', { month: 'long' });
        parts.push(`${month} ${filterState.monthPeriod.year}`);
      }
      break;
    case ReportPeriod.QUARTERLY:
      if (filterState.quarterPeriod) {
        parts.push(`Q${filterState.quarterPeriod.quarter} ${filterState.quarterPeriod.year}`);
      }
      break;
    case ReportPeriod.ANNUAL:
      if (filterState.yearPeriod) {
        parts.push(`${filterState.yearPeriod.fiscal ? 'Fiscal' : 'Calendar'} Year ${filterState.yearPeriod.year}`);
      }
      break;
    case ReportPeriod.CUSTOM:
      if (filterState.customDateRange) {
        const { start, end } = filterState.customDateRange;
        if (start && end) {
          parts.push(`${start} to ${end}`);
        } else if (start) {
          parts.push(`From ${start}`);
        } else if (end) {
          parts.push(`Until ${end}`);
        }
      }
      break;
  }
  
  // Comparison
  if (filterState.comparisonType !== ComparisonType.NONE) {
    switch (filterState.comparisonType) {
      case ComparisonType.MOM:
        parts.push('Month-over-Month comparison');
        break;
      case ComparisonType.YOY:
        parts.push('Year-over-Year comparison');
        break;
      case ComparisonType.QOQ:
        parts.push('Quarter-over-Quarter comparison');
        break;
      case ComparisonType.CUSTOM:
        parts.push('Custom period comparison');
        break;
    }
  }
  
  // Source filters
  if (filterState.sources.length > 0) {
    if (filterState.sources.length === 1) {
      parts.push(`Source: ${filterState.sources[0]}`);
    } else {
      parts.push(`Sources: ${filterState.sources.slice(0, 2).join(', ')}${filterState.sources.length > 2 ? ` +${filterState.sources.length - 2} more` : ''}`);
    }
  }
  
  // Campaign filters
  if (filterState.campaigns.length > 0) {
    if (filterState.campaigns.length === 1) {
      parts.push(`Campaign: ${filterState.campaigns[0]}`);
    } else {
      parts.push(`Campaigns: ${filterState.campaigns.slice(0, 2).join(', ')}${filterState.campaigns.length > 2 ? ` +${filterState.campaigns.length - 2} more` : ''}`);
    }
  }
  
  // Trade filters
  if (filterState.trades.length > 0) {
    if (filterState.trades.length === 1) {
      parts.push(`Trade: ${filterState.trades[0]}`);
    } else {
      parts.push(`Trades: ${filterState.trades.slice(0, 2).join(', ')}${filterState.trades.length > 2 ? ` +${filterState.trades.length - 2} more` : ''}`);
    }
  }
  
  // Metric thresholds
  if (filterState.metricThresholds.length > 0) {
    filterState.metricThresholds.forEach(threshold => {
      let operator: string;
      switch (threshold.operator) {
        case 'gt': operator = '>'; break;
        case 'lt': operator = '<'; break;
        case 'eq': operator = '='; break;
        case 'gte': operator = '>='; break;
        case 'lte': operator = '<='; break;
        default: operator = threshold.operator;
      }
      
      parts.push(`${threshold.metric} ${operator} ${threshold.value}`);
    });
  }
  
  return parts.join(' • ');
};

/**
 * Create presets for quick filter selection
 * @returns Object with preset filters
 */
export const getFilterPresets = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  return {
    // Time period presets
    thisMonth: {
      reportPeriod: ReportPeriod.MONTHLY,
      monthPeriod: { month: currentMonth, year: currentYear }
    },
    
    lastMonth: {
      reportPeriod: ReportPeriod.MONTHLY,
      monthPeriod: { 
        month: currentMonth === 1 ? 12 : currentMonth - 1,
        year: currentMonth === 1 ? currentYear - 1 : currentYear
      }
    },
    
    thisQuarter: {
      reportPeriod: ReportPeriod.QUARTERLY,
      quarterPeriod: { quarter: Math.ceil(currentMonth / 3) as 1 | 2 | 3 | 4, year: currentYear }
    },
    
    lastQuarter: {
      reportPeriod: ReportPeriod.QUARTERLY,
      quarterPeriod: { 
        quarter: Math.ceil(currentMonth / 3) === 1 ? 4 : (Math.ceil(currentMonth / 3) - 1) as 1 | 2 | 3,
        year: Math.ceil(currentMonth / 3) === 1 ? currentYear - 1 : currentYear
      }
    },
    
    thisYear: {
      reportPeriod: ReportPeriod.ANNUAL,
      yearPeriod: { year: currentYear, fiscal: false }
    },
    
    lastYear: {
      reportPeriod: ReportPeriod.ANNUAL,
      yearPeriod: { year: currentYear - 1, fiscal: false }
    },
    
    // YTD
    yearToDate: {
      reportPeriod: ReportPeriod.CUSTOM,
      customDateRange: {
        start: `${currentYear}-01-01`,
        end: new Date().toISOString().split('T')[0]
      }
    },
    
    // Last 30 days
    last30Days: {
      reportPeriod: ReportPeriod.CUSTOM,
      customDateRange: {
        start: new Date(currentDate.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      }
    },
    
    // Last 90 days
    last90Days: {
      reportPeriod: ReportPeriod.CUSTOM,
      customDateRange: {
        start: new Date(currentDate.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      }
    },
    
    // Fiscal Year to Date (assuming Oct-Sep fiscal year)
    fiscalYearToDate: {
      reportPeriod: ReportPeriod.CUSTOM,
      customDateRange: {
        start: currentMonth >= 10 ? `${currentYear}-10-01` : `${currentYear - 1}-10-01`,
        end: new Date().toISOString().split('T')[0]
      }
    },
  };
};

export default {
  monthPeriodToDateRange,
  quarterPeriodToDateRange,
  yearPeriodToDateRange,
  getDateRangeFromFilterState,
  getComparisonDateRange,
  filterStateToQueryParams,
  createSavedView,
  generateFilterSummary,
  getFilterPresets
};